import { ArrowLeft, ClipboardCheck, Cloud, ExternalLink, LockKeyhole, ShieldCheck, Smartphone, Truck, UserRound } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { Button, Field } from '../components/shared'

const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL?.trim() || 'https://alco-log-app.pages.dev/'
const publicAppLabel = publicAppUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')

export default function LoginScreen({ drivers, onLogin, cloudConfigured = false, onCloudLogin }) {
  const [role, setRole] = useState('driver')
  const [driverId, setDriverId] = useState(drivers[0]?.id || '')
  const [mode, setMode] = useState('demo')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [cloudError, setCloudError] = useState('')
  const [cloudBusy, setCloudBusy] = useState(false)

  const submit = async (event) => {
    event.preventDefault()
    if (mode === 'cloud') {
      setCloudError('')
      setCloudBusy(true)
      try { await onCloudLogin(email, password) }
      catch (error) { setCloudError(error.message || 'ログインできませんでした') }
      finally { setCloudBusy(false) }
      return
    }
    onLogin({ role, userId: role === 'driver' ? driverId : 'admin-01' })
  }

  return (
    <main className="login-page">
      <section className="login-intro">
        <div className="brand-mark brand-mark--large"><ShieldCheck /><span>ALCO LOG</span></div>
        <div>
          <h1>運転前の安全確認を、<br />一つの流れでつなぐ。</h1>
          <p>配送ドライバーの安全点呼、日常車両点検、アルコールチェックを車両情報とともに管理します。</p>
        </div>
        <ul className="login-points">
          <li><ClipboardCheck /><span><strong>安全点呼を一体化</strong>体調、疲労、睡眠不足とアルコール確認</span></li>
          <li><Truck /><span><strong>車両異常を整備へつなぐ</strong>ブレーキ、異音、警告灯などを記録</span></li>
          <li><LockKeyhole /><span><strong>証跡を残す</strong>月報、CSV、印刷・PDF保存</span></li>
        </ul>
      </section>
      <section className="login-panel">
        <div className="login-panel__header">
          <h2>ログイン</h2>
          <p>{mode === 'cloud' ? '会社から発行されたアカウントでログインします。' : 'デモ用の利用者を選んで開始してください。'}</p>
        </div>
        {mode === 'demo' ? <div className="role-switch" aria-label="利用者区分">
          <button className={role === 'driver' ? 'active' : ''} onClick={() => setRole('driver')}><UserRound />ドライバー</button>
          <button className={role === 'admin' ? 'active' : ''} onClick={() => setRole('admin')}><ShieldCheck />管理者</button>
        </div> : <div className="cloud-login-heading"><Cloud /><span><strong>クラウド運用</strong>Supabase認証・共有データ</span></div>}
        <form onSubmit={submit}>
          {mode === 'cloud' ? <>
            <Field label="メールアドレス" required><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" /></Field>
            <Field label="パスワード" required><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" /></Field>
          </> : role === 'driver' ? (
            <Field label="ドライバー名" required>
              <select value={driverId} onChange={(event) => setDriverId(event.target.value)}>
                {drivers.filter((driver) => driver.active).map((driver) => <option value={driver.id} key={driver.id}>{driver.name}（{driver.employeeId}）</option>)}
              </select>
            </Field>
          ) : (
            <div className="demo-account"><ShieldCheck /><span><strong>鈴木 管理者</strong>安全管理部／全社管理</span></div>
          )}
          {mode === 'demo' ? <Field label="パスワード" hint="デモのため入力内容は検証しません">
            <input type="password" defaultValue="demopass" autoComplete="current-password" />
          </Field> : null}
          {cloudError ? <p className="inline-error">{cloudError}</p> : null}
          <Button type="submit" className="button--full" disabled={cloudBusy}>{mode === 'cloud' ? cloudBusy ? 'ログイン中…' : 'クラウドへログイン' : role === 'driver' ? 'チェックを開始' : '管理画面を開く'}</Button>
        </form>
        {mode === 'demo' ? <p className="login-notice"><LockKeyhole />体験モードのデータはこのブラウザだけに保存され、実運用データとは混ざりません。</p> : null}
        {cloudConfigured ? <button className="cloud-login-switch" onClick={() => { setMode(mode === 'demo' ? 'cloud' : 'demo'); setCloudError('') }}>{mode === 'demo' ? <><Cloud />クラウド運用アカウントでログイン</> : <><ArrowLeft />体験モードに戻る</>}</button> : <p className="cloud-ready-note"><Cloud />公開先ではこの体験モードをそのまま利用できます。Supabase接続後は共有データ運用も有効になります。</p>}
        <section className="login-qr" aria-labelledby="login-qr-title">
          <div className="login-qr__code" aria-label="スマホ用ログインページQRコード">
            <QRCodeSVG value={publicAppUrl} size={168} level="M" bgColor="#ffffff" fgColor="#0f172a" marginSize={4} title="ALCO LOGをスマホで開くQRコード" />
          </div>
          <div className="login-qr__body">
            <span className="login-qr__icon"><Smartphone /></span>
            <div>
              <strong id="login-qr-title">スマホですぐに確認</strong>
              <p>iPhoneの標準カメラで読み取ってください。読み取れない場合はURLから直接開けます。</p>
              <a href={publicAppUrl} target="_blank" rel="noreferrer">URLを直接開く<ExternalLink /></a>
              <span className="login-qr__url">{publicAppLabel}</span>
            </div>
          </div>
        </section>
      </section>
    </main>
  )
}
